// frontend/src/pages/profile.jsx
import { useEffect, useState, useContext } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getLockStatusMap } from '../utils/courseLock';
import DeliverablesContent from '../components/DeliverablesContent';
import { XMarkIcon } from '@heroicons/react/24/outline';


function Profile() {
  const [user, setUser] = useState(null);
  const [units, setUnits] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState(null);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  /** 'documents' | 'course' - when 'documents', main area shows Documents tab content */
  const [leftPanelTab, setLeftPanelTab] = useState('course');
  const { isAuthenticated, authLoading, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [showCourseWelcome, setShowCourseWelcome] = useState(true);

  useEffect(() => {
    if (isAuthenticated === false) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true); // Start loading
      const fetchData = async () => {
        try {
          // Fetch profile, units, chapters, and progress
          const [userRes, unitsRes, chaptersRes, progressRes] = await Promise.all([
            fetch(`${import.meta.env.VITE_API_URL}/api/auth/profile`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${import.meta.env.VITE_API_URL}/api/units`),
            fetch(`${import.meta.env.VITE_API_URL}/api/chapters?list=1`),
            fetch(`${import.meta.env.VITE_API_URL}/api/user/progress`, { // Fetches answers and completedChapters
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

          if (userRes.ok && unitsRes.ok && chaptersRes.ok && progressRes.ok) {
            const userData = await userRes.json();
            const unitsData = await unitsRes.json();
            const chaptersData = await chaptersRes.json();
            const progressData = await progressRes.json();

            setUser(userData.user);
            setUnits(unitsData);
            setChapters(chaptersData);
            // Ensure progress is always set, even if empty (for new users with no progress)
            setProgress(progressData || { progress: {}, completedChapters: [] });
            
            // Restore selected unit from localStorage, or default to first unit
            if (unitsData.length > 0) {
              const savedUnitId = localStorage.getItem('selectedUnitId');
              // Check if saved unit ID exists in the current units list
              const validSavedUnit = savedUnitId && unitsData.find(u => u._id === savedUnitId);
              setSelectedUnitId(validSavedUnit ? savedUnitId : unitsData[0]._id);
            }
          } else {
            console.error('Error fetching data: One or more requests failed.');
            if (userRes.status === 401 || progressRes.status === 401) {
              console.error('Unauthorized access detected.');
              navigate('/login');
            } else {
              // Set default empty progress to prevent "Failed to load" error
              setProgress({ progress: {}, completedChapters: [] });
            }
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false); // Stop loading regardless of outcome
        }
      };

      fetchData();
    }
  }, [isAuthenticated, token, navigate]);

  // Refresh progress when returning to profile page (e.g., from chapter page)
  useEffect(() => {
    if (isAuthenticated && token && location.pathname === '/profile') {
      const refreshProgress = async () => {
        try {
          const progressRes = await fetch(`${import.meta.env.VITE_API_URL}/api/user/progress`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (progressRes.ok) {
            const progressData = await progressRes.json();
            setProgress(progressData || { progress: {}, completedChapters: [] });
          }
        } catch (error) {
          console.error('Error refreshing progress:', error);
        }
      };
      refreshProgress();
    }
  }, [location.pathname, isAuthenticated, token]);

  // Role-based filtering: student/pro only
  const getFilteredChapters = (chaptersList) => {
    if (!user) return chaptersList;
    
    // Admin sees everything
    if (user.role === 'admin') return chaptersList;
    
    // Student sees all chapters (no filtering needed)
    if (user.role === 'student') return chaptersList;
    
    // Pro sees all chapters
    if (user.role === 'pro') return chaptersList;
    
    // Default: show all for other roles
    return chaptersList;
  };

  const lockStatusMap = getLockStatusMap(units, chapters, progress, user);

  const isChapterLocked = (chapterId) => {
    if (user?.role === 'admin') return false;
    const id = (chapterId?.toString?.() ?? chapterId);
    return lockStatusMap.chapters[id] ?? true;
  };

  const isModuleLocked = (moduleId) => {
    if (user?.role === 'admin') return false;
    const id = (moduleId?.toString?.() ?? moduleId);
    return lockStatusMap.modules[id] ?? true;
  };

  const isUnitLocked = (unitId) => {
    if (user?.role === 'admin') return false;
    const id = (unitId?.toString?.() ?? unitId);
    return lockStatusMap.units[id] ?? true;
  };

  // Helper function to clean chapter titles for display
  const formatDisplayTitle = (title) => {
    if (!title) return '';
    const prefixRegex = /^(workshop|chapter|module)\s?\d+:\s*/i;
    return title.replace(prefixRegex, '').trim();
  };

  // Use the combined loading state
  if (loading || authLoading || isAuthenticated === null) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-white">
        <div className="w-80 mb-4">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-black rounded-full transition-all duration-1000 ease-out"
              style={{
                width: '85%',
                animation: 'loadingProgress 0.25s ease-out forwards'
              }}
            />
          </div>
        </div>
        <style jsx>{`
          @keyframes loadingProgress {
            0% { width: 0% }
            50% { width: 60% }
            80% { width: 85% }
            100% { width: 95% }
          }
        `}</style>
      </div>
    );
  }

  // Handle case where data fetching failed but didn't redirect
  if (!user || !progress) {
    return <div className="m-20 text-center text-red-500">Failed to load profile data. Please try again later.</div>;
  }

  // Organize chapters by unit and module
  const organizeChaptersByHierarchy = () => {
    const hierarchy = {};
    
    units.forEach(unit => {
      hierarchy[unit._id] = {
        unit,
        modules: {}
      };
    });

    chapters.forEach(chapter => {
      if (!chapter.moduleId || !chapter.moduleId._id) return;
      const moduleId = chapter.moduleId._id;
      const unitId = chapter.moduleId.unitId?._id || chapter.moduleId.unitId;
      
      if (!hierarchy[unitId]) return;
      
      if (!hierarchy[unitId].modules[moduleId]) {
        hierarchy[unitId].modules[moduleId] = {
          module: chapter.moduleId,
          chapters: []
        };
      }
      
      hierarchy[unitId].modules[moduleId].chapters.push(chapter);
    });

    // Sort chapters within each module and convert modules to sorted arrays
    Object.values(hierarchy).forEach(unitData => {
      // Convert modules object to array and sort by module order
      const modulesArray = Object.values(unitData.modules);
      modulesArray.sort((a, b) => {
        const orderA = a.module?.order ?? 0;
        const orderB = b.module?.order ?? 0;
        return orderA - orderB;
      });
      
      // Store sorted modules array
      unitData.modulesArray = modulesArray;
      
      // Sort chapters within each module
      modulesArray.forEach(moduleData => {
        moduleData.chapters.sort((a, b) => a.order - b.order);
      });
    });

    return hierarchy;
  };

  const hierarchy = organizeChaptersByHierarchy();
  const selectedUnit = units.find(u => u._id === selectedUnitId);
  const selectedUnitData = selectedUnit ? hierarchy[selectedUnitId] : null;

  // Get color for module based on module.color field or default
  const getModuleColor = (module) => {
    const colorMap = {
      gray: { bg: 'bg-gray-50', border: 'border-gray-300', darkBg: 'dark:bg-gray-600/20', darkBorder: 'dark:border-gray-500' },
      blue: { bg: 'bg-blue-50', border: 'border-blue-300', darkBg: 'dark:bg-blue-600/20', darkBorder: 'dark:border-blue-500' },
      green: { bg: 'bg-green-50', border: 'border-green-300', darkBg: 'dark:bg-green-600/20', darkBorder: 'dark:border-green-500' },
      purple: { bg: 'bg-purple-50', border: 'border-purple-300', darkBg: 'dark:bg-purple-600/20', darkBorder: 'dark:border-purple-500' },
      yellow: { bg: 'bg-yellow-50', border: 'border-yellow-300', darkBg: 'dark:bg-yellow-600/20', darkBorder: 'dark:border-yellow-500' },
      pink: { bg: 'bg-pink-50', border: 'border-pink-300', darkBg: 'dark:bg-pink-400/20', darkBorder: 'dark:border-pink-500' },
      indigo: { bg: 'bg-indigo-50', border: 'border-indigo-300', darkBg: 'dark:bg-indigo-600/20', darkBorder: 'dark:border-indigo-500' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-300', darkBg: 'dark:bg-orange-600/20', darkBorder: 'dark:border-orange-500' },
      teal: { bg: 'bg-teal-50', border: 'border-teal-300', darkBg: 'dark:bg-teal-600/20', darkBorder: 'dark:border-teal-500' },
    };
    
    const moduleColor = module?.color || 'gray';
    return colorMap[moduleColor] || colorMap.gray;
  };

  // Determine if user needs upgrade (student role for pro content)
  const needsUpgrade = (chapter) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'pro') return false;
    // For now, students can access all content (no upgrade needed)
    // This can be customized based on business logic
    return false;
  };

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      <div className="max-w-8xl mx-auto w-full min-w-0 pt-8 px-4 sm:px-6 lg:px-8">
        {/* Always render the courses view — min-w-0 so main column can shrink beside fixed Oberon panel */}
        <div className="flex min-w-0 w-full flex-col md:flex-row md:space-x-8">
          {/* Vertical Navigation (Courses View + Documents tab) */}
          <aside className="md:w-1/4 lg:w-1/5 mb-8 md:mb-0 shrink-0">
            <nav className="space-y-1" aria-label="Sidebar">
              <button
                onClick={() => {
                  setLeftPanelTab('documents');
                }}
                className={`w-full flex items-center px-3 py-2 font-body text-md rounded-md transition-colors duration-150 ease-in-out text-left ${
                  leftPanelTab === 'documents'
                    ? 'bg-gray-200 dark:bg-gray-700 text-black dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
                aria-current={leftPanelTab === 'documents' ? 'page' : undefined}
              >
                <span className="truncate">Documents</span>
              </button>
              {units.map(unit => {
                const unitIsLocked = isUnitLocked(unit._id);
                return (
                  <button
                    key={unit._id}
                    onClick={() => {
                      if (!unitIsLocked) {
                        setLeftPanelTab('course');
                        setSelectedUnitId(unit._id);
                        // Save selected unit to localStorage for persistence
                        localStorage.setItem('selectedUnitId', unit._id);
                      }
                    }}
                    disabled={unitIsLocked}
                    className={`w-full flex items-center justify-between px-3 py-2 font-body text-md rounded-md transition-colors duration-150 ease-in-out text-left ${
                      unitIsLocked
                        ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60'
                        : leftPanelTab === 'course' && selectedUnitId === unit._id
                        ? 'bg-gray-200 dark:bg-gray-700 text-black dark:text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                    aria-current={leftPanelTab === 'course' && selectedUnitId === unit._id ? 'page' : undefined}
                    title={unitIsLocked ? 'Complete all modules in the previous unit to unlock' : unit.name}
                  >
                    <span className="truncate">{unit.name}</span>
                    {unitIsLocked && <span className="ml-2 text-xs">🔒</span>}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main Content Area (Documents tab or Courses View) */}
          <main className="flex-1 min-w-0">
            {leftPanelTab === 'documents' ? (
              <div className="min-w-0 max-w-full">
                <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-6 text-gray-900 dark:text-white">
                  Documents
                </h2>
                <DeliverablesContent showHeading={false} />
              </div>
            ) : selectedUnitData ? (
              <div className="min-w-0 max-w-full">
                {showCourseWelcome && (
                <div
                  className="relative mb-5 rounded-lg border border-blue-100/90 dark:border-blue-900/40 bg-blue-50/70 dark:bg-blue-950/20 px-3 py-3 pr-9 sm:px-4 sm:py-3.5 sm:pr-10"
                  role="region"
                  aria-label="Course welcome"
                >
                  <button
                    type="button"
                    onClick={() => setShowCourseWelcome(false)}
                    className="absolute top-2 right-2 rounded-md p-1 text-gray-500 hover:bg-blue-100/80 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-blue-900/40 dark:hover:text-gray-100"
                    aria-label="Dismiss welcome message"
                  >
                    <XMarkIcon className="h-4 w-4" aria-hidden />
                  </button>
                  <p className="font-heading text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5 leading-snug pr-1">
                    Welcome to Makerstate: We provide business and entrepreneurship education for creators and artists.
                  </p>
                  <p className="font-body text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    To support your learning, your instructor has access to your responses throughout this course.
                    Your usage information, in an aggregated format, may be used to help us improve the course experience for future students.
                  </p>
                </div>
                )}
                <h2 className="text-2xl font-heading mb-6 text-gray-800 dark:text-gray-200">
                  {selectedUnit.name}
                </h2>
                {selectedUnit.description && (
                  <p className="text-gray-600 dark:text-gray-400 mb-6">{selectedUnit.description}</p>
                )}
                
                {/* Modules and Chapters */}
                {(selectedUnitData.modulesArray || Object.values(selectedUnitData.modules)).map(moduleData => {
                  const moduleId = moduleData.module._id || moduleData.module;
                  const moduleIsLocked = isModuleLocked(moduleId);
                  
                  return (
                    <div key={moduleId} className={`mb-8 ${moduleIsLocked ? 'opacity-60' : ''}`}>
                      <div className="flex items-center gap-2 mb-4">
                        <h3 className="text-xl font-heading text-gray-700 dark:text-gray-300">
                          {moduleData.module.name}
                        </h3>
                        {moduleIsLocked && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200" title="Complete all chapters in the previous module to unlock">
                            🔒 Locked
                          </span>
                        )}
                      </div>
                      {moduleData.module.description && (
                        <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                          {moduleData.module.description}
                        </p>
                      )}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-1 xl:grid-cols-2 gap-6">
                      {moduleData.chapters.map((chapter, idx) => {
                        // Normalize chapter ID to string for consistent lookup
                        const chapterIdStr = typeof chapter._id === 'object' ? chapter._id.toString() : chapter._id;
                        const chapterProgressData = progress?.progress?.[chapterIdStr];
                        
                        const isCompleted = chapterProgressData?.status === 'completed';
                        const isInProgress = chapterProgressData?.status === 'in_progress';
                        const completionPercentage = chapterProgressData?.completionPercentage || 0;
                        // Show progress if there's any progress data (in_progress, completed, or has answers)
                        const hasProgress = chapterProgressData && (
                          chapterProgressData.status === 'in_progress' || 
                          chapterProgressData.status === 'completed' ||
                          (chapterProgressData.answers && Object.keys(chapterProgressData.answers).length > 0) ||
                          completionPercentage > 0
                        );
                        
                        const currentNeedsUpgrade = needsUpgrade(chapter);
                        // Chapter is locked if module is locked OR chapter itself is locked
                        const isLocked = user.role === 'admin' 
                          ? false 
                          : (currentNeedsUpgrade ? true : (moduleIsLocked || isChapterLocked(chapter._id)));
                        
                        const canStart = !isCompleted && !isLocked;
                        const canReview = isCompleted && !isLocked;
                        const canGetFeedback = isCompleted && !isLocked;

                        const moduleColor = getModuleColor(moduleData.module);
                        
                        return (
                          <div
                            key={chapter._id}
                            className={`${moduleColor.bg} ${moduleColor.darkBg} ${moduleColor.border} ${moduleColor.darkBorder} border-2 rounded-md shadow-sm flex flex-col justify-between p-5 transition-opacity opacity-100`}
                          >
                            <div>
                              <div className="flex justify-between items-start mb-2">
                                <h3 className="font-heading text-lg text-gray-800 dark:text-gray-200">
                                  {`${idx + 1}. ${formatDisplayTitle(chapter.title)}`}
                                </h3>
                                <div className="flex-shrink-0 ml-2 font-body flex gap-1 flex-wrap">
                                  {/* Required/Optional Badge */}
                                  {chapter.isRequired !== false ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200" title="Required">Required</span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200" title="Optional">Optional</span>
                                  )}
                                  {isLocked && !currentNeedsUpgrade && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200" title="Locked">🔒 Locked</span>
                                  )}
                                  {currentNeedsUpgrade && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200" title="Upgrade Required">✨ Pro</span>
                                  )}
                                  {isCompleted && !isLocked && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200" title="Completed">✓ Completed</span>
                                  )}
                                  {isInProgress && !isCompleted && !isLocked && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200" title="In Progress">In Progress</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-4">
                              {/* Progress Status - Bottom Left */}
                              {hasProgress && !isLocked && (
                                <div className="flex-1 mr-4">
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Progress</span>
                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{completionPercentage}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full transition-all duration-300 ${
                                        isCompleted 
                                          ? 'bg-green-500 dark:bg-green-600' 
                                          : 'bg-blue-500 dark:bg-blue-600'
                                      }`}
                                      style={{ width: `${completionPercentage}%` }}
                                    ></div>
                                  </div>
                                </div>
                              )}
                              {/* Action Buttons - Bottom Right */}
                              <div className="font-body flex items-center justify-end space-x-3 flex-shrink-0">
                                {currentNeedsUpgrade ? (
                                  <Link to="/pricing" className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent shadow-sm text-sm rounded-md text-white bg-[#51c0a6] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#51c0a6] transition">Upgrade to Pro</Link>
                                ) : (
                                  <>
                                    {canGetFeedback && (
                                      <Link to={`/feedback/${chapter._id}`} className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition">Feedback</Link>
                                    )}
                                    <div className="relative inline-block">
                                      <Link
                                        to={(canStart || canReview) ? `/chapter/${chapter._id}` : '#'}
                                        onClick={(e) => {
                                          if (!(canStart || canReview)) e.preventDefault();
                                        }}
                                        aria-disabled={isLocked}
                                        className={`
                                          peer
                                          inline-flex items-center justify-center gap-2
                                          px-3 py-1.5 text-sm rounded-md shadow-sm
                                          transition
                                          ${isLocked
                                            ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed border border-transparent text-white'
                                            : 'bg-black dark:bg-gray-700 text-white border border-transparent hover:bg-gray-800 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}
                                        `}
                                      >
                                        {hasProgress && !isCompleted && <span>↩</span>}
                                        {isCompleted ? 'Review' : (hasProgress ? 'Resume' : 'Start')}
                                      </Link>

                                      {isLocked && (
                                        <span
                                          className="
                                            absolute left-1/2 top-full mt-1
                                            -translate-x-1/2
                                            whitespace-nowrap rounded bg-gray-800 px-2 py-1
                                            text-sm text-white
                                            opacity-0 peer-hover:opacity-100 peer-focus:opacity-100
                                            transition-opacity duration-150
                                            z-[80]
                                          "
                                        >
                                          Please complete the previous chapter before moving on.
                                        </span>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
                })}
              </div>
            ) : null}
          </main>
        </div>

        {/* Call to Action (Show only for students) */}
        {user.role === 'student' && (
          <div className="mt-16 p-8 rounded-lg border border-gray-200 text-center bg-white shadow-sm">
            <h2 className="text-xl font-heading mb-3 text-gray-800">Unlock Full Access</h2>
            <p className="font-body mb-6 text-gray-600">
              Ready to dive deeper? Upgrade to Pro to access all modules, our AI copilot, and personalized coaching.
            </p>
            {/* <Link
              to="/pricing"
              className="inline-block bg-black text-white px-6 py-2.5 rounded-md font-body shadow-sm"
            >
              View Pricing Plans
            </Link> */}
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
